import axios, { AxiosResponse } from "axios";
import { IBaseModel } from "../interfaces/base";
import {
  IPartnerStorePartnerRequestModel,
  IPartnerStoreUpdateResponseModel,
  IStorePartner,
  IStorePartnerResponseModel,
} from "../interfaces/configuration";
import {
  IPartnerMenuCommonResponseModel,
  IPartnerMenuItemListResponseModel,
  IPartnerMenuItemRequestModel,
  IPartnerMenuItemUpdateRequestModel,
} from "../interfaces/menu";
import {
  IPartnerOrderRequestModel,
  IPartnerOrderListResponseModel,
  IPartnerOrderResponseModel,
  IPOSOrderStatusResponseModel,
  IPartnerOrderListRequestModel,
  ICancelOrDenyReasonResponseModel,
  IPartnerOrderSearchByReferenceResponseModel,
  IPartnerReportRequestModel,
} from "../interfaces/order";
import {
  IPatchCartOrderItemRequestModel,
  IPatchCartResponseModel,
} from "../interfaces/patchCart";
import {
  IGetPartnerStoreListResponseModel,
  IPartnerProvisionCommonResponseModel,
  IPartnerProvisonRequestModel,
} from "../interfaces/provision";
import {
  IGetStoreStatusResponseModel,
  IPartnerStoreCommonResponseModel,
  IPartnerStoreRequestModel,
} from "../interfaces/store";
import { IUserRequestModel, IUserResponseModel } from "../interfaces/user";

axios.defaults.baseURL = process.env.REACT_APP_API_URL; //from .env
axios.defaults.headers.common["Authorization"] =
  "Bearer " + process.env.REACT_APP_AUTH_TOKEN; //from .env
axios.defaults.headers.common["TimeStamp"] = "125635753";
axios.defaults.headers.post["Content-Type"] = "application/json";

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

//creating generic types for rest API calls

const restApiCalls = {
  get: <T>(url: string) => axios.get<T>(url).then(responseBody),
  post: <T>(url: string, body: {}) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T>(url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
};

//user related api calls
const Users = {
  validateLogin: (userRequestModel: IUserRequestModel) =>
    restApiCalls.post<IUserResponseModel>(
      "/Login/ValidateLogin",
      userRequestModel
    ),
};

//order related api calls
const Orders = {
  fetchPartnerOrderList: (
    partnerOrderListRequestModel: IPartnerOrderListRequestModel
  ) =>
    restApiCalls.post<IPartnerOrderListResponseModel>(
      "PartnerOrder/GetPartnerOrderList",
      partnerOrderListRequestModel
    ),
  fetchPartnerOrder: (partnerOrderRequestModel: IPartnerOrderRequestModel) =>
    restApiCalls.post<IPartnerOrderResponseModel>(
      "PartnerOrder/GetPartnerOrder",
      partnerOrderRequestModel
    ),
  getPrintBill: (partnerOrderRequestModel: IPartnerOrderRequestModel) =>
    restApiCalls.post<IPartnerOrderResponseModel>(
      "PartnerOrder/GetPrints",
      partnerOrderRequestModel
    ),
  fetchOrderStatus: (baseRequestModel: IBaseModel) =>
    restApiCalls.post<IPOSOrderStatusResponseModel>(
      "PartnerOrder/GetPOSOrderStatus",
      baseRequestModel
    ),
  fetchOrderStatusForBtn: (
    posOrderStatusRequestModel: IPartnerOrderRequestModel
  ) =>
    restApiCalls.post<IPOSOrderStatusResponseModel>(
      "PartnerOrder/GetRelevantPOSOrderStatusByOrderStatusID",
      posOrderStatusRequestModel
    ),
  fetchOrderCountByOrderStatus: (
    posOrderCountRequestModel: IPartnerOrderRequestModel
  ) =>
    restApiCalls.post<IPartnerOrderListResponseModel>(
      "PartnerOrder/GetPartnerOrderCountByPOSStatus",
      posOrderCountRequestModel
    ),
  updateOrderStatus: (posOrderStatusRequestModel: IPartnerOrderRequestModel) =>
    restApiCalls.post<IPOSOrderStatusResponseModel>(
      "PartnerOrder/UpdatePartnerOrderStatus",
      posOrderStatusRequestModel
    ),
  fetchCancelDenyReasonCodes: (
    cancelDenyReasonRequestModel: IPartnerOrderRequestModel
  ) =>
    restApiCalls.post<ICancelOrDenyReasonResponseModel>(
      "PartnerOrder/GetReasonCodesForCancelOrDeny",
      cancelDenyReasonRequestModel
    ),
  patchCart: (
    patchCartOrderItemRequestModel: IPatchCartOrderItemRequestModel
  ) =>
    restApiCalls.post<IPatchCartResponseModel>(
      "PartnerOrder/PatchCart",
      patchCartOrderItemRequestModel
    ),
  searchByOrderReference: (
    searchByOrderReferenceRequestModel: IPartnerOrderRequestModel
  ) =>
    restApiCalls.post<IPartnerOrderSearchByReferenceResponseModel>(
      "PartnerOrder/SearchPartnerOrdersByOrderReference",
      searchByOrderReferenceRequestModel
    ),
  getXReport: (partnerReportRequestModel: IPartnerReportRequestModel) =>
    restApiCalls.post<IPartnerOrderResponseModel>(
      "PartnerOrder/GetSalesReport",
      partnerReportRequestModel
    ),

  getPickMeOrders: (orderRequestModel: IPartnerOrderRequestModel) =>
    restApiCalls.post<IPartnerOrderResponseModel>(
      "PartnerOrder/GetPickmeOrderList",
      orderRequestModel
    ),
};

//menu related api calls
const Menus = {
  getMenuItemList: (baseRequestModel: IBaseModel) =>
    restApiCalls.post<IPartnerMenuItemListResponseModel>(
      "PartnerMenu/GetPartnerItemListToUpload",
      baseRequestModel
    ),
  /*       uploadBulkMenuItemList: (
        menuListRequestModel: IPartnerMenuItemRequestModel
      ) =>
        restApiCalls.post<IPartnerMenuCommonResponseModel>(
          "PartnerMenu/UploadBulkMenuItemsInPartnerSide",
          menuListRequestModel
        ), */
  uploadBulkMenuItemList: (
    menuListRequestModel: IPartnerMenuItemRequestModel
  ) =>
    restApiCalls.post<IPartnerMenuCommonResponseModel>(
      "PartnerMenuBulkUpload/UploadBulkMenuItemsInPartnerSide",
      menuListRequestModel
    ),
  syncShopifyMenu: (menuListRequestModel: IPartnerMenuItemRequestModel) =>
    restApiCalls.post<IPartnerMenuCommonResponseModel>(
      "PartnermenubulkUpload/GetShopifyItemsToUpdate",
      menuListRequestModel
    ),
  /*  uploadToPartnerSide: (menuListRequestModel: IBaseModel) =>
    restApiCalls.post<IPartnerMenuCommonResponseModel>(
      "PartnerMenu/UploadMenuItemsPartnerSide",
      menuListRequestModel
    ), */
  updateItem: (
    menuItemUpdateRequestModel: IPartnerMenuItemUpdateRequestModel
  ) =>
    restApiCalls.post<IPartnerMenuCommonResponseModel>(
      "PartnerMenu/UpdateMenuItemInPartnerSide",
      menuItemUpdateRequestModel
    ),
};

//store related api calls
const Stores = {
  setStoreStatus: (storeStatusRequestModel: IPartnerStoreRequestModel) =>
    restApiCalls.post<IPartnerStoreCommonResponseModel>(
      "PartnerStore/SetPartnerStoreStatus",
      storeStatusRequestModel
    ),
  getStoreStatus: (baseRequestModel: IBaseModel) =>
    restApiCalls.post<IGetStoreStatusResponseModel>(
      "PartnerStore/GetPartnerStoreStatus",
      baseRequestModel
    ),
};

//provision related api calls
const Provisions = {
  setupProvision: (setupProvisionRequestModel: IPartnerProvisonRequestModel) =>
    restApiCalls.post<IPartnerProvisionCommonResponseModel>(
      "PartnerProvision/SetupPartnerProvision",
      setupProvisionRequestModel
    ),
  isUberProvisioned: (
    setupProvisionRequestModel: IPartnerProvisonRequestModel
  ) =>
    restApiCalls.post<IPartnerProvisionCommonResponseModel>(
      "PartnerProvision/CheckIsUberProvisioned",
      setupProvisionRequestModel
    ),
  getPartnerStoreList: (baseRequestModel: IBaseModel) =>
    restApiCalls.post<IGetPartnerStoreListResponseModel>(
      "PartnerProvision/GetPartnerStoreList",
      baseRequestModel
    ),
  provisionEnableDisble: (
    setupProvisionRequestModel: IPartnerProvisonRequestModel
  ) =>
    restApiCalls.post<IPartnerProvisionCommonResponseModel>(
      "PartnerProvision/HandleStoreProvisioning",
      setupProvisionRequestModel
    ),
};

const configuration = {
  getStorePartners: (baseRequestModel: IBaseModel) =>
    restApiCalls.post<IStorePartnerResponseModel>(
      "PartnerStore/GetStorePartners",
      baseRequestModel
    ),

  updateStorePartners: (updateRequestModel: IPartnerStorePartnerRequestModel) =>
    restApiCalls.post<IPartnerStoreUpdateResponseModel>(
      "PartnerStore/UpdateStorePartnersStatus",
      updateRequestModel
    ),
};

const apis = {
  Users,
  Orders,
  Menus,
  Stores,
  Provisions,
  configuration,
};

export default apis;
